import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "lf_cookie_consent_v1";

const COPY = {
  fr: { title: "Un seul cookie. Technique uniquement.", body: "LeaseFlowr utilise un unique cookie essentiel pour maintenir votre session (7 jours, httpOnly). Aucun tracking publicitaire, aucun cookie tiers.", learn: "En savoir plus", accept: "J'ai compris", close: "Fermer" },
  en: { title: "One cookie. Strictly technical.", body: "LeaseFlowr uses a single essential cookie to keep your session active (7 days, httpOnly). No ad tracking, no third-party cookies.", learn: "Learn more", accept: "Got it", close: "Close" },
  es: { title: "Una sola cookie. Solo técnica.", body: "LeaseFlowr usa una única cookie esencial para mantener tu sesión activa (7 días, httpOnly). Sin tracking publicitario, sin cookies de terceros.", learn: "Más información", accept: "Entendido", close: "Cerrar" },
  de: { title: "Ein einziger Cookie. Rein technisch.", body: "LeaseFlowr verwendet einen einzigen essentiellen Cookie, um Ihre Sitzung aktiv zu halten (7 Tage, httpOnly). Kein Werbe-Tracking, keine Drittanbieter-Cookies.", learn: "Mehr erfahren", accept: "Verstanden", close: "Schließen" },
};
function getCopy(lang) {
  const c = (lang || "fr").slice(0, 2).toLowerCase();
  return COPY[c] || COPY.fr;
}

/**
 * Minimal CNIL-friendly cookie banner.
 * We use only a strictly-necessary session cookie (httpOnly, 7d) — no tracking.
 * Per CNIL, strictly-necessary cookies don't require consent, but users expect
 * a clear notice. This banner:
 *  - Appears once until dismissed (persisted in localStorage)
 *  - Explains that only a technical cookie is used
 *  - Links to /legal#privacy for full details
 *  - Stays hidden on /legal and /login (no point showing there)
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const t = getCopy(i18n.language);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Tiny delay so the banner doesn't fight with page-entry animations
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage unavailable (private mode, etc.) — just show once per session
      setVisible(true);
    }
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted_at: new Date().toISOString() }));
    } catch { /* noop */ }
    setVisible(false);
  }

  // Don't distract on legal/login pages, or on the public demo (no popup on arrival)
  if (
    pathname.startsWith("/legal") ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/demo")
  )
    return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-[88px] left-4 right-4 sm:bottom-4 sm:left-6 sm:right-auto sm:max-w-[440px] z-50 pointer-events-auto"
          data-testid="cookie-banner"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.25)] dark:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.6)] p-4 sm:p-5 relative overflow-hidden">
            <button
              type="button"
              onClick={dismiss}
              aria-label={t.close}
              data-testid="cookie-banner-close"
              className="absolute top-2 right-2 w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3 pr-6">
              <div className="w-9 h-9 rounded-full bg-[#d4a373]/15 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                <Cookie className="w-4 h-4 text-[#d4a373] dark:text-amber-300" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-900 dark:text-slate-100 mb-1">
                  {t.title}
                </div>
                <p className="text-[12px] text-slate-600 dark:text-slate-400 leading-[1.55]">
                  {t.body}
                  {" "}
                  <Link
                    to="/legal#privacy"
                    data-testid="cookie-banner-learn-more"
                    className="text-[#4F7CFF] dark:text-[#7B9BFF] hover:underline font-medium"
                  >
                    {t.learn}
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-end gap-2 pl-12">
              <button
                type="button"
                onClick={dismiss}
                data-testid="cookie-banner-accept"
                className="px-3.5 py-1.5 text-[12px] font-medium text-white bg-[#4F7CFF] hover:bg-[#3B5BD9] rounded-md transition-colors"
              >
                {t.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
